import md5 from 'md5';

const API_CONFIG = {
  baseUrl: '/api'  // 修改为相对路径
};

// 生成签名
export const generateSign = (params) => {
  // 从 localStorage 获取用户的 API 密钥
  const appId = localStorage.getItem('appId');
  const appSecret = localStorage.getItem('appSecret');

  if (!appId || !appSecret) {
    throw new Error('未找到 API 密钥，请重新登录');
  }

  // 1. 按字段名升序排序并过滤掉 sign
  const sortedParams = Object.keys(params)
    .filter(key => key !== 'sign')
    .sort()
    .reduce((acc, key) => {
      acc[key] = params[key];
      return acc;
    }, {});

  // 2. 拼接参数，格式：key1=value1&key2=value2
  let signStr = Object.entries(sortedParams)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  
  // 3. 拼接 appid.secret
  signStr += `&${appId}.${appSecret}`;

  console.log('Sign string:', signStr); // 用于调试

  // 4. MD5 加密
  return md5(signStr);
};

// 获取公共参数
export const getCommonParams = () => {
  const appId = localStorage.getItem('appId');
  if (!appId) {
    throw new Error('未找到 API 密钥，请重新登录');
  }

  const timestamp = Math.floor(Date.now() / 1000);
  const params = {
    appid: appId,
    timestamp,
    sign_type: 'MD5'
  };
  
  const sign = generateSign(params);
  return { ...params, sign };
};

// 从 localStorage 获取 API 密钥
const getApiCredentials = () => {
  const appId = localStorage.getItem('appId');
  const appSecret = localStorage.getItem('appSecret');
  return { appId, appSecret };
};

// 单点登录请求
export const getLoginToken = async (username, password) => {
  const timestamp = Math.floor(Date.now() / 1000);
  
  const params = {
    appid: localStorage.getItem('appId'),
    timestamp,
    sign_type: 'MD5',
    user: username,
    password
  };

  console.log('Login request params:', params);

  const sign = generateSign(params);
  const queryString = Object.entries({ ...params, sign })
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  try {
    // 获取登录令牌
    const response = await fetch(`${API_CONFIG.baseUrl}/login-token?${queryString}`);
    const data = await response.json();
    console.log('Login response data:', data);
    
    if (data.error_code === 0 && data.data?.login_token) {
      // 直接使用 login_token
      const token = data.data.login_token;
      localStorage.setItem('rbtoken', token);
      localStorage.setItem('isAuthenticated', 'true');
      console.log('Login token saved:', token);
      
      return data.data;
    } else {
      console.error('Login response has no login_token:', data);
      throw new Error(data.error_msg || '登录失败');
    }
  } catch (error) {
    console.error('Login request failed:', error);
    throw error;
  }
};

// 获取分类数据
export const getClassificationData = async (entity, field) => {
  const timestamp = Math.floor(Date.now() / 1000);
  
  const params = {
    appid: localStorage.getItem('appId'),
    timestamp,
    sign_type: 'MD5',
    entity,  // 使用传入的实体名称，而不是固定的 'Account'
    field
  };

  const sign = generateSign(params);
  const queryString = Object.entries({ ...params, sign })
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  console.log('Classification request params:', params);  // 添加调试日志

  try {
    const response = await fetch(`${API_CONFIG.baseUrl}/metadata/classification-data?${queryString}`);
    const data = await response.json();
    
    console.log(`Classification data for ${field}:`, data);
    
    if (data.error_code === 0) {
      return data.data;
    } else {
      console.error('Classification data error:', data.error_msg);  // 添加错误日志
      throw new Error(data.error_msg);
    }
  } catch (error) {
    console.error('Get classification data failed:', error);  // 添加错误日志
    throw error;
  }
};

// 获取实体数据
export const getEntityData = async (entity, params = {}) => {
  try {
    const { appId, appSecret } = getApiCredentials();
    if (!appId || !appSecret) {
      throw new Error('未找到 API 密钥，请重新登录');
    }

    const timestamp = Math.floor(Date.now() / 1000);
    
    const queryParams = {
      appid: appId,
      timestamp,
      sign_type: 'MD5',
      entity,
      fields: params.fields || 'id,name',
      page_no: params.page || 1,
      page_size: params.pageSize || 10,
      conditions: []
    };

    // 添加搜索条件
    if (params.searchText && params.searchText.trim()) {
      const searchField = entity === 'huanzhegenjinmingxi' ? 'huanzhe' : 'huanzhexingming';
      queryParams.conditions.push({
        field: searchField,
        operator: 'contains',
        value: params.searchText.trim()
      });
    }

    // 将 conditions 转换为字符串
    if (queryParams.conditions.length > 0) {
      queryParams.conditions = JSON.stringify(queryParams.conditions);
    } else {
      delete queryParams.conditions;
    }

    const sign = generateSign(queryParams);
    const formData = new URLSearchParams({ ...queryParams, sign }).toString();
    
    const response = await fetch(`${API_CONFIG.baseUrl}/entity/list`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`请求失败: ${response.status}`);
    }

    const data = await response.json();
    console.log('Entity list response:', data);
    
    if (data.error_code === 0) {
      return {
        list: data.data.list,
        total: data.data.total
      };
    } else {
      throw new Error(data.error_msg || '获取数据失败');
    }
  } catch (error) {
    console.error('Get entity data failed:', error);
    throw error;
  }
};

// 处理分类数据
const processClassificationData = (data) => {
  if (!Array.isArray(data)) return {};
  const map = {};
  const process = (items) => {
    items.forEach(item => {
      if (item && item.id) {
        // 如果是对象，使用 text 属性，否则直接使用值
        map[item.id] = typeof item.text === 'object' ? item.text.text : item.text;
      }
      if (item && Array.isArray(item.children)) {
        process(item.children);
      }
    });
  };
  process(data);
  return map;
};

// 获取医院列表
export const getHospitalList = async (params) => {
  const timestamp = Math.floor(Date.now() / 1000);
  
  const queryParams = {
    appid: localStorage.getItem('appId'),
    timestamp,
    sign_type: 'MD5',
    entity: 'yiyuanshujuku',
    fields: 'yiyuanshujukuNo,yiyuanmingcheng,yiyuanleixing,yiyuanjibie,diqu,chuangweishu,chengliriqi,lianxidianhua,dizhi,shifouwanglaiyiyuan,yiyuanwangzhi,yiyuanxuhao,jianchengbiecheng',
    page_no: params.page || 1,
    page_size: params.pageSize || 10,
    conditions: []
  };

  // 添加搜索条件
  if (params.searchText) {
    queryParams.conditions.push({
      field: 'yiyuanmingcheng',
      operator: 'contains',
      value: params.searchText
    });
  }

  if (params.type) {
    queryParams.conditions.push({
      field: 'yiyuanleixing',
      operator: 'eq',
      value: params.type
    });
  }

  if (params.level) {
    queryParams.conditions.push({
      field: 'yiyuanjibie',
      operator: 'eq',
      value: params.level
    });
  }

  // 将 conditions 转换为字符串
  if (queryParams.conditions.length > 0) {
    queryParams.conditions = JSON.stringify(queryParams.conditions);
  } else {
    delete queryParams.conditions;
  }

  const sign = generateSign(queryParams);
  
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}/entity/list`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: Object.entries({ ...queryParams, sign })
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&')
    });

    const data = await response.json();
    
    if (data.error_code === 0) {
      return {
        list: data.data.list.map(item => ({
          id: item.yiyuanshujukuNo,
          name: item.yiyuanmingcheng,
          type: item.yiyuanleixing?.id || item.yiyuanleixing,
          level: item.yiyuanjibie?.id || item.yiyuanjibie,
          region: typeof item.diqu === 'object' ? item.diqu.id : item.diqu,
          beds: item.chuangweishu,
          establishTime: item.chengliriqi,
          phone: item.lianxidianhua,
          address: item.dizhi,
          shortName: item.jianchengbiecheng,
          isOutpatient: item.shifouwanglaiyiyuan,
          website: item.yiyuanwangzhi,
          serialNo: item.yiyuanxuhao
        })),
        total: data.data.total
      };
    } else {
      throw new Error(data.error_msg || '获取医院列表失败');
    }
  } catch (error) {
    console.error('Get hospital list failed:', error);
    throw error;
  }
};

// 获取仪表盘统计数据
export const getHospitalStats = async () => {
  try {
    // 获取医院列表数据用于统计
    const result = await getHospitalList(1, 99999);
    const hospitals = result.list || [];
    
    // 获取当前日期和本月初日期
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    
    // 统计数据
    const stats = {
      totalHospitals: hospitals.length,
      totalAreas: new Set(hospitals.map(h => h.diqu?.id || h.diqu)).size,
      todayNew: hospitals.filter(h => {
        const createdDate = new Date(h.createdOn);
        return createdDate >= today;
      }).length,
      monthNew: hospitals.filter(h => {
        const createdDate = new Date(h.createdOn);
        return createdDate >= monthStart;
      }).length,
      weekOnWeek: {
        hospitals: 12,    // 示例数据，实际应该计算
        areas: 12,
        today: 12,
        month: 12
      },
      dayOnDay: {
        hospitals: -11,   // 示例数据，实际应该计算
        areas: -11,
        today: -11,
        month: -11
      }
    };

    return stats;
  } catch (error) {
    console.error('获取统计数据失败:', error);
    throw new Error('获取统计数据失败');
  }
};

// 获取下拉列表数据
export const getPicklistData = async (entity, field) => {
  const timestamp = Math.floor(Date.now() / 1000);
  
  const params = {
    appid: localStorage.getItem('appId'),
    timestamp,
    sign_type: 'MD5',
    entity,
    field
  };

  const sign = generateSign(params);
  const queryString = Object.entries({ ...params, sign })
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  console.log('Picklist request params:', params);

  try {
    const response = await fetch(`${API_CONFIG.baseUrl}/metadata/picklist-data?${queryString}`);
    const data = await response.json();
    
    console.log(`Picklist data for ${field}:`, data);
    
    if (data.error_code === 0) {
      return data.data;  // 直接返回数据数组
    } else {
      console.error('Picklist data error:', data.error_msg);
      throw new Error(data.error_msg);
    }
  } catch (error) {
    console.error('Get picklist data failed:', error);
    throw error;
  }
};

// 获取实体列表
export const getEntities = async () => {
  const timestamp = Math.floor(Date.now() / 1000);
  
  const params = {
    appid: localStorage.getItem('appId'),
    timestamp,
    sign_type: 'MD5'
  };

  const sign = generateSign(params);
  const queryString = Object.entries({ ...params, sign })
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  try {
    const response = await fetch(`${API_CONFIG.baseUrl}/metadata/entities?${queryString}`);
    const data = await response.json();
    
    if (data.error_code === 0) {
      return data.data;
    } else {
      throw new Error(data.error_msg || '获取实体列表失败');
    }
  } catch (error) {
    console.error('Get entities failed:', error);
    throw error;
  }
};

// 获取实体字段列表
export const getEntityFields = async (entity) => {
  const timestamp = Math.floor(Date.now() / 1000);
  
  const params = {
    appid: localStorage.getItem('appId'),
    timestamp,
    sign_type: 'MD5',
    entity
  };

  const sign = generateSign(params);
  const queryString = Object.entries({ ...params, sign })
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  try {
    const response = await fetch(`${API_CONFIG.baseUrl}/metadata/fields?${queryString}`);
    const data = await response.json();
    
    if (data.error_code === 0) {
      return data.data;
    } else {
      throw new Error(data.error_msg || '获取字段列表失败');
    }
  } catch (error) {
    console.error('Get entity fields failed:', error);
    throw error;
  }
};

// AI 分析接口
export const getAIAnalysis = async (prompt) => {
  try {
    const response = await fetch(process.env.REACT_APP_AI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_AI_API_KEY}`,
      },
      body: JSON.stringify({
        model: "glm-4-plus",
        messages: [
          {
            role: "system",
            content: "你是一位经验丰富的心血管外科专家，请根据提供的检查指标进行专业的分析和建议。"
          },
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 2000,
        stream: false
      })
    });

    if (!response.ok) {
      throw new Error(`API 请求失败: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error('AI Analysis Error:', error);
    throw new Error('AI 分析失败，请稍后重试');
  }
}; 